import React, { useMemo, useState, useCallback } from 'react';
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  Animated,
  ViewStyle,
} from 'react-native';
import { createStyles } from './DbContainer.styles';
import type {
  DbContainerProps,
  DbSectionProps,
  DbCardProps,
  DbRowProps,
  DbColProps,
  DbSpacerProps,
} from './DbContainer.types';

// 解析padding/margin
const parsePadding = (value?: number | { top?: number; right?: number; bottom?: number; left?: number }) => {
  if (value === undefined) return {};
  if (typeof value === 'number') {
    return { padding: value };
  }
  return {
    paddingTop: value.top,
    paddingRight: value.right,
    paddingBottom: value.bottom,
    paddingLeft: value.left,
  };
};

const parseMargin = (value?: number | { top?: number; right?: number; bottom?: number; left?: number }) => {
  if (value === undefined) return {};
  if (typeof value === 'number') {
    return { margin: value };
  }
  return {
    marginTop: value.top,
    marginRight: value.right,
    marginBottom: value.bottom,
    marginLeft: value.left,
  };
};

// 基础容器组件
export const DbContainer: React.FC<DbContainerProps> = ({
  children,
  variant = 'default',
  direction = 'column',
  justify,
  align,
  wrap,
  gap,
  padding,
  margin,
  radius,
  background,
  borderColor,
  borderWidth,
  shadow,
  fullWidth,
  width,
  height,
  flex,
  style,
  onPress,
  onLongPress,
  disabled,
  accessibilityLabel,
}) => {
  const styles = useMemo(() => createStyles('light'), []);

  const containerStyle: ViewStyle[] = [styles.container];

  // 变体样式
  if (variant === 'card') {
    containerStyle.push(styles.card);
    if (shadow) {
      if (shadow === 'sm' || shadow === true) {
        containerStyle.push(styles.cardShadowSm);
      } else if (shadow === 'md') {
        containerStyle.push(styles.cardShadowMd);
      } else if (shadow === 'lg') {
        containerStyle.push(styles.cardShadowLg);
      }
    }
  } else if (variant === 'section') {
    containerStyle.push(styles.section);
  } else if (variant === 'flex') {
    containerStyle.push(styles.flex);
  }

  // 布局属性
  const layoutStyle: ViewStyle = {
    flexDirection: direction,
    justifyContent: justify,
    alignItems: align,
    flexWrap: wrap,
    gap,
    borderRadius: radius,
    backgroundColor: background,
    borderColor,
    borderWidth,
    width: width as any,
    height: height as any,
    flex,
  };

  // 其他属性
  if (fullWidth) {
    containerStyle.push(styles.fullWidth);
  }
  if (disabled) {
    containerStyle.push(styles.disabled);
  }

  const finalStyle = [
    ...containerStyle,
    layoutStyle,
    parsePadding(padding),
    parseMargin(margin),
    style,
  ];

  if (onPress || onLongPress) {
    return (
      <TouchableOpacity
        style={finalStyle}
        onPress={disabled ? undefined : onPress}
        onLongPress={disabled ? undefined : onLongPress}
        activeOpacity={1}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel}
      >
        {children}
      </TouchableOpacity>
    );
  }

  return (
    <View style={finalStyle} accessibilityLabel={accessibilityLabel}>
      {children}
    </View>
  );
};

// Section 组件
export const DbSection: React.FC<DbSectionProps> = ({
  children,
  title,
  titleStyle,
  extra,
  showDivider = false,
  icon,
  ...containerProps
}) => {
  const styles = useMemo(() => createStyles('light'), []);

  return (
    <DbContainer variant="section" {...containerProps}>
      {(title || extra) && (
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderLeft}>
            {icon && <View style={styles.sectionIcon}>{icon}</View>}
            {title && <Text style={[styles.sectionTitle, titleStyle]}>{title}</Text>}
          </View>
          {extra}
        </View>
      )}
      {showDivider && <View style={styles.sectionDivider} />}
      <View style={styles.sectionContent}>{children}</View>
    </DbContainer>
  );
};

// Card 组件
export const DbCard: React.FC<DbCardProps> = ({
  children,
  title,
  titleStyle,
  subtitle,
  subtitleStyle,
  headerExtra,
  footer,
  cover,
  coverHeight = 160,
  collapsible = false,
  defaultExpanded = true,
  onExpandChange,
  shadow = 'sm',
  ...containerProps
}) => {
  const styles = useMemo(() => createStyles('light'), []);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const rotateAnim = React.useRef(new Animated.Value(defaultExpanded ? 1 : 0)).current;

  const handleToggle = useCallback(() => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    onExpandChange?.(newExpanded);

    Animated.spring(rotateAnim, {
      toValue: newExpanded ? 1 : 0,
      useNativeDriver: true,
    }).start();
  }, [expanded, onExpandChange, rotateAnim]);

  const rotate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const hasHeader = title || subtitle || headerExtra || collapsible;

  return (
    <DbContainer variant="card" shadow={shadow} {...containerProps}>
      {cover && (
        <Image
          source={{ uri: cover }}
          style={[styles.cardCover, { height: coverHeight }]}
        />
      )}
      
      {hasHeader && (
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={collapsible ? handleToggle : undefined}
          activeOpacity={collapsible ? 0.7 : 1}
        >
          <View style={styles.cardHeaderContent}>
            {title && <Text style={[styles.cardTitle, titleStyle]}>{title}</Text>}
            {subtitle && <Text style={[styles.cardSubtitle, subtitleStyle]}>{subtitle}</Text>}
          </View>
          {headerExtra}
          {collapsible && (
            <Animated.View style={[styles.cardCollapse, { transform: [{ rotate }] }]}>
              <Text>▼</Text>
            </Animated.View>
          )}
        </TouchableOpacity>
      )}
      
      {(!collapsible || expanded) && (
        <View style={styles.cardBody}>{children}</View>
      )}
      
      {footer && (!collapsible || expanded) && (
        <View style={styles.cardFooter}>{footer}</View>
      )}
    </DbContainer>
  );
};

// Row 组件 (水平布局)
export const DbRow: React.FC<DbRowProps> = ({
  children,
  center,
  align = 'center',
  ...containerProps
}) => {
  return (
    <DbContainer
      variant="flex"
      direction="row"
      align={center ? 'center' : align}
      justify={center ? 'center' : containerProps.justify}
      {...containerProps}
    >
      {children}
    </DbContainer>
  );
};

// Col 组件 (垂直布局)
export const DbCol: React.FC<DbColProps> = ({
  children,
  center,
  span,
  flex: flexProp,
  ...containerProps
}) => {
  const flexValue = span ? span / 24 : flexProp;
  
  return (
    <DbContainer
      variant="flex"
      direction="column"
      align={center ? 'center' : containerProps.align}
      justify={center ? 'center' : containerProps.justify}
      flex={flexValue}
      {...containerProps}
    >
      {children}
    </DbContainer>
  );
};

// Spacer 组件 (占位)
export const DbSpacer: React.FC<DbSpacerProps> = ({
  width,
  height,
  flex,
}) => {
  const styles = useMemo(() => createStyles('light'), []);

  return (
    <View
      style={[
        styles.spacer,
        { width, height, flex },
      ]}
    />
  );
};

export default DbContainer;
export type {
  DbContainerProps,
  DbSectionProps,
  DbCardProps,
  DbRowProps,
  DbColProps,
  DbSpacerProps,
};
